import { useState } from "react";
import { Download, Store, ShoppingBag, RefreshCw, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

// /admin/exports — one-click marketplace bulk-upload files (Depop + Walmart).
export default function AdminExports() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [downloading, setDownloading] = useState("");
  const [wmStatus, setWmStatus] = useState(null);
  const [wmBusy, setWmBusy] = useState(false);

  const wmCall = async (method, path) => {
    if (!adminKey) { toast.error("Enter your admin key first."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setWmBusy(true);
    try {
      const { data } = method === "post"
        ? await api.post(path, {}, { headers: { "X-Admin-Key": adminKey } })
        : await api.get(path, { headers: { "X-Admin-Key": adminKey } });
      if (data.ok === false) { toast.error(data.error || "Walmart connection failed"); return; }
      return data;
    } catch {
      toast.error("Walmart request failed — check your admin key.");
    } finally {
      setWmBusy(false);
    }
  };

  const wmRefresh = async () => {
    const d = await wmCall("get", "/walmart/status");
    if (d) setWmStatus(d);
  };

  const wmSync = async () => {
    const d = await wmCall("post", "/walmart/sync");
    if (d) {
      toast.success(`Synced stock & prices for ${d.matched} Walmart items`);
      wmRefresh();
    }
  };

  const download = async (channel) => {
    if (!adminKey) { toast.error("Enter your admin key first."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setDownloading(channel);
    try {
      const base = process.env.REACT_APP_BACKEND_URL;
      const r = await fetch(`${base}/api/exports/${channel}.csv`, { headers: { "X-Admin-Key": adminKey } });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `${channel}-bulk-upload.csv`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`${channel === "depop" ? "Depop" : "Walmart"} file downloaded`);
    } catch {
      toast.error("Download failed — check your admin key.");
      localStorage.removeItem(KEY_STORE);
    }
    setDownloading("");
  };

  const channels = [
    {
      id: "depop", name: "Depop", icon: ShoppingBag,
      blurb: "Bulk-listing CSV for Depop's official tool",
      steps: ["Open Depop on desktop → Selling Hub → Bulk Listing", "Upload this CSV", "Review the drafts Depop creates", "Fix any flagged rows, then hit Post"],
    },
    {
      id: "walmart", name: "Walmart Marketplace", icon: Store,
      blurb: "Custom spreadsheet for Seller Center bulk upload",
      steps: ["Needs an approved Walmart seller account first", "Seller Center → Add items → Upload your own spreadsheet", "Upload this CSV and map the columns when prompted", "Products need UPC/GTIN codes — or request Walmart's free GTIN exemption during setup"],
    },
  ];

  return (
    <div className="max-w-[900px] mx-auto px-5 py-10 font-body" data-testid="admin-exports">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl">Marketplace Exports</h1>
        <input
          type="password"
          value={adminKey}
          onChange={(e) => setAdminKey(e.target.value)}
          placeholder="Admin key"
          data-testid="admin-exports-key"
          className="border border-[#E5E5DF] px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#B84A3B]"
        />
      </div>
      <p className="text-sm text-[#6E6E69] mt-2 max-w-xl">
        Fresh files generated from your live catalog every download — always current stock and prices.
        Only in-stock women's items are included.
      </p>

      <div className="mt-8 grid sm:grid-cols-2 gap-5">
        {channels.map((c) => (
          <div key={c.id} className="border border-[#E5E5DF] p-6" data-testid={`export-card-${c.id}`}>
            <c.icon size={22} className="text-[#B84A3B]" />
            <h2 className="font-display text-xl mt-3">{c.name}</h2>
            <p className="text-xs font-mono text-[#6E6E69] mt-1">{c.blurb}</p>
            <ol className="mt-4 space-y-1.5 text-[0.8rem] text-[#6E6E69] list-decimal list-inside">
              {c.steps.map((s) => <li key={s}>{s}</li>)}
            </ol>
            <button
              data-testid={`download-${c.id}`}
              onClick={() => download(c.id)}
              disabled={downloading === c.id}
              className="mt-5 w-full bg-[#1A1A1A] text-white py-3 text-[0.68rem] font-mono tracking-[0.18em] uppercase hover:bg-[#B84A3B] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              <Download size={13} /> {downloading === c.id ? "Preparing…" : `Download ${c.name} CSV`}
            </button>
          </div>
        ))}
      </div>

      <div className="mt-8 border border-[#E5E5DF] p-6" data-testid="walmart-sync-card">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl flex items-center gap-2">
              <Store size={18} className="text-[#B84A3B]" /> Walmart Live Sync
            </h2>
            <p className="text-xs font-mono text-[#6E6E69] mt-1">Direct API connection — stock levels and prices push straight to your Walmart listings</p>
          </div>
          <div className="flex gap-2">
            <button data-testid="walmart-check" onClick={wmRefresh} disabled={wmBusy}
              className="border border-[#1A1A1A] px-4 py-2.5 text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#EFEFEA] transition-colors disabled:opacity-50">
              Check Connection
            </button>
            <button data-testid="walmart-sync" onClick={wmSync} disabled={wmBusy}
              className="bg-[#1A1A1A] text-white px-4 py-2.5 text-[0.65rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] transition-colors flex items-center gap-2 disabled:opacity-50">
              <RefreshCw size={12} className={wmBusy ? "animate-spin" : ""} /> Sync Stock & Prices
            </button>
          </div>
        </div>
        {wmStatus && (
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="walmart-status">
            {[
              ["On Walmart", wmStatus.walmart_items],
              ["In your catalog", wmStatus.catalog_items],
              ["Synced (matched)", wmStatus.matched_existing],
              ["Not on Walmart yet", wmStatus.new_to_publish],
            ].map(([label, val]) => (
              <div key={label} className="bg-[#EFEFEA] p-3 text-center">
                <div className="font-display text-2xl">{val}</div>
                <div className="text-[0.6rem] font-mono tracking-wide uppercase text-[#6E6E69] mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        )}
        {wmStatus?.ok && (
          <p className="mt-3 text-[0.72rem] text-[#2F5D3A] flex items-center gap-1.5" data-testid="walmart-ok">
            <CheckCircle2 size={13} /> Connected to your Walmart seller account.
          </p>
        )}
        <p className="mt-3 text-[0.72rem] text-[#6E6E69] leading-relaxed">
          Note: your Walmart account currently allows 250 total items and you already have 273 listed.
          Adding new products is paused until you request a limit increase (Seller Center → Help →
          &quot;Request catalog/item limit increase&quot; — usually approved for accounts in good standing).
          Stock and price sync for existing listings works now.
        </p>
      </div>
    </div>
  );
}
